

























































































import { l3draggable, l3droppable } from '@/directives/l3dragDrop';
import { Component, Vue, Prop, PropSync, Watch } from 'vue-property-decorator';
// components
import SimpleMessageBox from './SimpleMessageBox.vue';
import TusFileUploadOverlay from './TusFileUploadOverlay.vue';
import VideoTrimmerDlg from './VideoTrimmerDlg.vue';
// interfaces
import { FileDetailsVm } from '@/interfaces/FileDetailsVm';
import { namespace } from 'vuex-class';
import { CustomizationVm } from '@/interfaces/CustomizationVm';

const customization = namespace("customization");

@Component({
  components: {
    TusFileUploadOverlay,
    SimpleMessageBox,
    VideoTrimmerDlg
  },
  directives: { l3draggable, l3droppable }
})
export default class VideoWithDnD extends Vue {
  @PropSync("video", { type: String, default: "" }) syncedVideo!: string | null;
  @PropSync("videoStart", { type: String, default: null }) syncedVideoStart!: string | null;
  @PropSync("videoEnd", { type: String, default: null }) syncedVideoEnd!: string | null;
  @Prop() relativePath!: string; // optional relative path to upload file to
  @Prop({ type: [Boolean], default: false }) allowDropOnVideo!: boolean;
  @Prop({ type: [Boolean], default: false }) emitDeleteEvent!: boolean;
  @Prop({ type: [Boolean], default: false }) fillContainer!: boolean;
  @Prop({ type: [Boolean], default: false }) uploadVideo!: boolean;
  @Prop({ type: [Number], default: null }) previewHeight!: number | null;
  @Prop({ type: String, default: 'center', validator: (v: string) => ['start','top','center','middle','end','bottom'].includes(v) })
  verticalAlign!: string;
  @Prop({ type: [Number], default: 300 }) maxVideoWidth!: number;
  @Prop({ type: [Number], default: 300 }) maxVideoHeight!: number;
  @Prop({ type: [Boolean], default: false }) disabled!: boolean;

  @customization.Getter customizationSettings!: CustomizationVm;

  showTrimmer = false;
  videoDisplayWidth: number | null = null;
  fileToUpload: File | null = null;
  showConfirmDelete = false;
  videoLoadError: boolean = false;
  showDropNotAllowed: boolean = false;

  get videoSrcWithTrim(): string {
    if (!this.syncedVideo) return "";
    if (this.syncedVideoStart || this.syncedVideoEnd) {
      const start = this.syncedVideoStart || "0";
      const end = this.syncedVideoEnd || "";
      return `${this.syncedVideo}#t=${start}${end ? ',' + end : ''}`;
    }
    return this.syncedVideo;
  }

  onSaveTrim(trim: { start: string, end: string }) {
    this.syncedVideoStart = trim.start;
    this.syncedVideoEnd = trim.end;
  }

  onTimeUpdate() {
    const video = this.$refs.videoPlayer as HTMLVideoElement;
    if (!video) return;

    const start = this.parseTimeString(this.syncedVideoStart);
    const end = this.parseTimeString(this.syncedVideoEnd);

    if (start > 0 && video.currentTime < start) {
      video.currentTime = start + 0.01;
    }

    if (end > 0 && video.currentTime > end) {
      video.pause();
      video.currentTime = start > 0 ? start : 0;
    }
  }

  onSeeking() {
    const video = this.$refs.videoPlayer as HTMLVideoElement;
    if (!video) return;

    const start = this.parseTimeString(this.syncedVideoStart);
    const end = this.parseTimeString(this.syncedVideoEnd);

    if (start > 0 && video.currentTime < start) {
      video.currentTime = start + 0.01;
    }

    if (end > 0 && video.currentTime > end) {
      video.currentTime = end;
    }
  }

  parseTimeString(timeStr: string | null): number {
    if (!timeStr) return 0;
    try {
      const parts = timeStr.split(':');
      if (parts.length !== 3) return 0;
      const hours = parseFloat(parts[0]);
      const minutes = parseFloat(parts[1]);
      const lastParts = parts[2].split('.');
      const seconds = parseFloat(lastParts[0]);
      const ms = lastParts[1] ? parseFloat("0." + lastParts[1]) : 0;
      return hours * 3600 + minutes * 60 + seconds + ms;
    } catch (e) {
      return 0;
    }
  }

  @Watch('syncedVideo', { immediate: true })
  onSyncedVideoChanged(val: string | null) {
    if (!val) {
      this.videoDisplayWidth = null;
      this.videoLoadError = false;
      return;
    }
  }

  @Watch('previewHeight')
  onPreviewHeightChanged() {
    if (this.syncedVideo) {
      this.updateVideoDisplayWidth();
    }
  }

  updateVideoDisplayWidth() {
    const video = this.$refs.videoPlayer as HTMLVideoElement;
    if (video && video.videoWidth && video.videoHeight) {
      const aspect = video.videoWidth / video.videoHeight;
      this.videoDisplayWidth = this.previewHeight ? Math.max(1, Math.round(aspect * this.previewHeight)) : null;
      this.videoLoadError = false;
    } else {
      this.videoDisplayWidth = null;
    }
  }

  get containerStyle() {
    const alignMap: { [key: string]: string } = {
      start: 'flex-start',
      top: 'flex-start',
      center: 'center',
      middle: 'center',
      end: 'flex-end',
      bottom: 'flex-end'
    };
    const align = alignMap[this.verticalAlign] || 'center';

    if (this.previewHeight == null) {
      if (this.fillContainer) {
        const style: any = { display: 'flex', alignItems: align, justifyContent: 'center', width: '100%', height: '100%' };
        if (this.maxVideoWidth) style.maxWidth = `${this.maxVideoWidth}px`;
        if (this.maxVideoHeight) style.maxHeight = `${this.maxVideoHeight}px`;
        return style;
      }
      return { display: 'flex', alignItems: align, justifyContent: 'center', width: '100%', height: '100%' };
    }

    if (!this.syncedVideo) {
      return {
        display: 'flex',
        alignItems: align,
        justifyContent: 'center',
        width: `${this.maxVideoWidth}px`,
        height: `${this.previewHeight}px`
      };
    }

    if (this.fillContainer) {
      const style: any = {
        display: 'flex',
        alignItems: align,
        justifyContent: 'center',
        width: '100%',
        height: `${this.previewHeight}px`
      };
      if (this.maxVideoWidth) style.maxWidth = `${this.maxVideoWidth}px`;
      if (this.maxVideoHeight) style.maxHeight = `${this.maxVideoHeight}px`;
      return style;
    }

    return {
      display: 'flex',
      alignItems: align,
      justifyContent: 'center',
      width: this.videoDisplayWidth ? `${this.videoDisplayWidth}px` : 'auto',
      height: `${this.previewHeight}px`
    };
  }

  private isVideoDropData(data: any, dt: DataTransfer | null): boolean {
    if (data?.fileType && data.fileType === 'Video') return true;

    if (Array.isArray(data) && data[0]?.name && data[0]?.type) {
      return data[0].type.startsWith('video/');
    }

    if (!dt) return false;

    try {
      if (dt.items && dt.items.length) {
        for (const item of Array.from(dt.items)) {
          if (item?.kind === 'file') {
            const type = item.type || '';
            return type.startsWith('video/') || type === '';
          }
        }
      }

      if (dt.types && Array.from(dt.types).includes('Files')) {
        return true;
      }
    } catch (ex) {
      return false;
    }

    return false;
  }

  private isBlockedDropData(data: any, dt: DataTransfer | null): boolean {
    if (!this.syncedVideo || this.allowDropOnVideo || this.disabled) {
      return false;
    }

    return this.isVideoDropData(data, dt);
  }

  private updateDropState(data: any, dt: DataTransfer | null): boolean {
    const blocked = this.isBlockedDropData(data, dt);
    this.showDropNotAllowed = blocked;
    return blocked;
  }

  handleNativeDragEnter(e: DragEvent) {
    const blocked = this.updateDropState(undefined, e.dataTransfer);

    if (blocked) {
      e.preventDefault();
      e.stopPropagation();
      try {
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'none';
          e.dataTransfer.effectAllowed = 'none';
        }
      } catch {}
      return;
    }

    this.showDropNotAllowed = false;
  }

  handleNativeDragOver(e: DragEvent) {
    const blocked = this.updateDropState(undefined, e.dataTransfer);

    if (blocked) {
      e.preventDefault();
      e.stopPropagation();
      try {
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'none';
          e.dataTransfer.effectAllowed = 'none';
        }
      } catch {}
      return;
    }

    this.showDropNotAllowed = false;
  }

  handleNativeDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.showDropNotAllowed = false;
  }

  handleNativeDragLeave() {
    this.showDropNotAllowed = false;
  }

  get shouldShowDropNotAllowed(): boolean {
    return this.showDropNotAllowed && !!this.syncedVideo && !this.allowDropOnVideo && !this.disabled;
  }

  get videoStyle() {
    return {
      height: this.previewHeight != null ? `${this.previewHeight}px` : (this.fillContainer ? '100%' : 'auto'),
      width: this.fillContainer ? '100%' : (this.videoDisplayWidth ? `${this.videoDisplayWidth}px` : 'auto'),
      maxWidth: '100%',
      maxHeight: '100%'
    };
  }

  onUploaded(filePath: string | null) {
    if (!filePath) return;
    this.syncedVideo = filePath;
    this.$emit('updateSceneImage:videowithdnd', this.syncedVideo);
    this.videoLoadError = false;
  }

  onCancelled() {
    this.fileToUpload = null;
  }

  openFileDialog() {
    const input = this.$refs.fileInput as HTMLInputElement | undefined;
    if (input) input.click();
  }

  onDownloadFile() {
    if (!this.syncedVideo) return;
    let filename = this.$globalHelper.extractFileName(this.syncedVideo);
    this.$globalHelper.download(this.syncedVideo, filename);
  }

  onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input || !input.files || input.files.length === 0) return;
    const file = input.files[0];

    if (!file.type.startsWith("video/")) {
      this.$store.commit("ux/SB_FAILURE", {
        message: "Bei dieser Datei handelt es sich nicht um ein Video.",
        timeout: 0
      });
      input.value = '';
      return;
    }

    if (this.uploadVideo) {
      this.fileToUpload = file;
    } else {
      const reader = new FileReader();
      reader.onload = (ev: any) => {
        this.syncedVideo = ev.target.result;
        this.$emit('updateSceneImage:videowithdnd', this.syncedVideo);
        input.value = '';
      };
      reader.readAsDataURL(file);
    }
  }

  onDeleteVideo() {
    if (this.emitDeleteEvent) {
      this.$emit('delete:videowithdnd');
      return;
    }
    this.syncedVideo = null;
    this.$emit('updateSceneImage:videowithdnd', this.syncedVideo);
    this.videoLoadError = false;
  }

  onVideoError() {
    this.videoLoadError = true;
    this.videoDisplayWidth = null;
  }

  onVideoLoad() {
    this.videoLoadError = false;
    this.updateVideoDisplayWidth();
  }

  get isDark(): boolean {
    try {
      return !!((this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark);
    } catch (e) {
      return false;
    }
  }

  get overlayStyle() {
    return {
      background: this.isDark ? 'rgba(50,50,50,0.85)' : 'rgba(200,200,200,0.85)'
    };
  }

  get dropOptions() {
    return {
      accept: (data: any, ev?: DragEvent) => {
        const hasFileTransfer = !!ev?.dataTransfer?.types?.includes('Files');
        const isVideoDrop = this.isVideoDropData(data, ev?.dataTransfer ?? null);
        const blocked = !!this.syncedVideo && !this.allowDropOnVideo && !this.disabled && isVideoDrop;
        const accepted = !blocked && (isVideoDrop || hasFileTransfer);
        return accepted;
      },
      hoverClass: "drop-hover",
      transferTypes: ["Files"],
      targetGroups: ["video-file-details"],
      onDragEnter: (ev?: DragEvent) => {
        this.updateDropState(undefined, ev?.dataTransfer ?? null);
      },
      onDragOver: (ev?: DragEvent) => {
        this.updateDropState(undefined, ev?.dataTransfer ?? null);
      },
      onDrop: async (data: any) => {
        if (data?.fileType && (data.fileType == "Video")) {
          let fileDetails: FileDetailsVm = data;
          this.syncedVideo = fileDetails.filePath;
          this.$emit('updateSceneImage:videowithdnd', this.syncedVideo);
          this.syncedVideoStart = fileDetails.videoStart;
          this.syncedVideoEnd = fileDetails.videoEnd;
        } else if (Array.isArray(data) && data[0] && data[0].name && data[0].type) {
          if (!data[0].type.startsWith("video/")) {
            this.$store.commit("ux/SB_FAILURE", {
              message: "Bei dieser Datei handelt es sich nicht um ein Video.",
              timeout: 0
            });
            return;
          }
          if (this.uploadVideo) {
            this.fileToUpload = data[0];
          } else {
            const file = data[0];
            const reader = new FileReader();
            reader.onload = async (e: any) => {
              this.syncedVideo = e.target.result;
              this.$emit('updateSceneImage:videowithdnd', this.syncedVideo);
            };
            reader.readAsDataURL(file);
          }
        }
      }
    };
  }
}
